<template>
  <div class="signing-toast-queue">
    <transition-group
      name="signing-toast"
      tag="div"
      class="signing-toast-queue__list"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="signing-toast"
        :class="'signing-toast--' + toast.status"
      >
        <!-- Icon -->
        <div
          class="signing-toast__icon"
          :class="'signing-toast__icon--' + toast.status"
        >
          <!-- Spinner for signing -->
          <div
            v-if="toast.status === 'signing'"
            class="signing-toast__spinner"
          ></div>
          <!-- Checkmark for success -->
          <svg
            v-else-if="toast.status === 'success'"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M5 13l4 4L19 7"
              stroke="#1d9e75"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <!-- Alert for error -->
          <svg
            v-else-if="toast.status === 'error'"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 9v4m0 4h.01M12 2L2 20h20L12 2z"
              stroke="#e24b4a"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <!-- Content -->
        <div class="signing-toast__content">
          <span
            class="signing-toast__title"
            :class="'signing-toast__title--' + toast.status"
          >
            {{ toastTitle(toast) }}
          </span>
          <span class="signing-toast__subtitle">
            {{ toastSubtitle(toast) }}
          </span>
        </div>

        <!-- Close -->
        <button class="signing-toast__close" @click="removeToast(toast.id)">
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path
              d="M1 1L9 9M9 1L1 9"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <!-- Progress bar (only while signing) -->
        <div v-if="toast.status === 'signing'" class="signing-toast__progress">
          <div
            class="signing-toast__progress-bar"
            :style="{ width: toast.progress + '%' }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { signingEventBus } from "@/services/signingEventBus";

export default {
  name: "SigningToastQueue",
  data() {
    return {
      toasts: [],
      progressTimers: {}
    };
  },
  methods: {
    toastTitle(toast) {
      if (toast.type === "download") {
        if (toast.status === "signing") return `Preparando ${toast.format}...`;
        if (toast.status === "success") return "Archivo descargado";
        if (toast.status === "error") return "Error al descargar";
      }
      if (toast.type === "email") {
        if (toast.status === "signing") return "Enviando correo...";
        if (toast.status === "success") return "Correo enviado";
        if (toast.status === "error") return "Error al enviar correo";
      }
      if (toast.status === "signing") return `Firmando ${toast.clientName}`;
      if (toast.status === "success") return "Firmada exitosamente";
      if (toast.status === "error") return "Error al firmar";
      return "";
    },
    toastSubtitle(toast) {
      if (toast.type === "download") {
        if (toast.status === "signing") return toast.filename;
        if (toast.status === "success") return toast.filename;
        if (toast.status === "error")
          return toast.errorMessage || "Error desconocido";
      }
      if (toast.type === "email") {
        const doc = toast.documentNumber ? ` · ${toast.documentNumber}` : "";
        if (toast.status === "signing") return `${toast.clientName}${doc}`;
        if (toast.status === "success") return `${toast.clientName}${doc}`;
        if (toast.status === "error")
          return toast.errorMessage || "Error desconocido";
      }
      if (toast.status === "signing") return toast.documentNumber;
      if (toast.status === "success")
        return `${toast.clientName} · ${toast.documentNumber}`;
      if (toast.status === "error")
        return toast.errorMessage || "Error desconocido";
      return "";
    },
    addDownloadToast({ id, filename, format }) {
      this.toasts = this.toasts.filter(t => t.id !== id);
      const toast = {
        id,
        type: "download",
        filename: filename || "",
        format: format || "archivo",
        status: "signing",
        progress: 0,
        errorMessage: ""
      };
      this.toasts.push(toast);
      this.startFakeProgress(id);
      this.bringToFront();
    },
    addEmailToast({ id, clientName, documentNumber }) {
      this.toasts = this.toasts.filter(t => t.id !== id);
      const toast = {
        id,
        type: "email",
        clientName: clientName || "",
        documentNumber: documentNumber || "",
        status: "signing",
        progress: 0,
        errorMessage: ""
      };
      this.toasts.push(toast);
      this.startFakeProgress(id);
      this.bringToFront();
    },
    bringToFront() {
      this.$nextTick(() => {
        if (this.$el) {
          document.body.appendChild(this.$el);
          this.$el.style.cssText =
            "position:fixed !important; bottom:16px !important; right:16px !important; z-index:2147483647 !important; pointer-events:none;";
        }
      });
    },
    addSigningToast({ id, clientName, documentNumber }) {
      // Remove existing toast for same document if any
      this.toasts = this.toasts.filter(t => t.id !== id);

      const toast = {
        id,
        clientName: clientName || "",
        documentNumber: documentNumber || "",
        status: "signing",
        progress: 0,
        errorMessage: ""
      };

      this.toasts.push(toast);
      this.startFakeProgress(id);
      this.bringToFront();
    },
    markSuccess({ id, clientName, documentNumber }) {
      const toast = this.toasts.find(t => t.id === id);
      if (toast) {
        this.stopFakeProgress(id);
        toast.status = "success";
        toast.progress = 100;
        if (clientName) toast.clientName = clientName;
        if (documentNumber) toast.documentNumber = documentNumber;

        setTimeout(() => {
          this.removeToast(id);
        }, 4000);
      }
    },
    markError({ id, message }) {
      const toast = this.toasts.find(t => t.id === id);
      if (toast) {
        this.stopFakeProgress(id);
        toast.status = "error";
        toast.errorMessage = message || "Error desconocido";
      }
    },
    removeToast(id) {
      this.stopFakeProgress(id);
      this.toasts = this.toasts.filter(t => t.id !== id);
    },
    startFakeProgress(id) {
      let progress = 0;
      this.progressTimers[id] = setInterval(() => {
        const toast = this.toasts.find(t => t.id === id);
        if (!toast || toast.status !== "signing") {
          this.stopFakeProgress(id);
          return;
        }
        // Slow down as we approach 90%
        const remaining = 90 - progress;
        const increment = Math.max(0.3, remaining * 0.04);
        progress = Math.min(90, progress + increment);
        toast.progress = progress;
      }, 300);
    },
    stopFakeProgress(id) {
      if (this.progressTimers[id]) {
        clearInterval(this.progressTimers[id]);
        delete this.progressTimers[id];
      }
    }
  },
  mounted() {
    document.body.appendChild(this.$el);
    // Force inline styles — cannot be overridden by any CSS
    this.$el.style.cssText =
      "position:fixed !important; bottom:16px !important; right:16px !important; z-index:2147483647 !important; pointer-events:none;";
    signingEventBus.$on("signing:start", this.addSigningToast);
    signingEventBus.$on("signing:done", this.markSuccess);
    signingEventBus.$on("signing:error", this.markError);
    signingEventBus.$on("email:start", this.addEmailToast);
    signingEventBus.$on("email:done", this.markSuccess);
    signingEventBus.$on("email:error", this.markError);
    signingEventBus.$on("download:start", this.addDownloadToast);
    signingEventBus.$on("download:done", this.markSuccess);
    signingEventBus.$on("download:error", this.markError);
  },
  beforeDestroy() {
    signingEventBus.$off("signing:start", this.addSigningToast);
    signingEventBus.$off("signing:done", this.markSuccess);
    signingEventBus.$off("signing:error", this.markError);
    signingEventBus.$off("email:start", this.addEmailToast);
    signingEventBus.$off("email:done", this.markSuccess);
    signingEventBus.$off("email:error", this.markError);
    signingEventBus.$off("download:start", this.addDownloadToast);
    signingEventBus.$off("download:done", this.markSuccess);
    signingEventBus.$off("download:error", this.markError);
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
    Object.keys(this.progressTimers).forEach(id => {
      clearInterval(this.progressTimers[id]);
    });
  }
};
</script>

<style lang="scss">
.signing-toast-queue {
  position: fixed !important;
  bottom: 16px !important;
  right: 16px !important;
  z-index: 2147483647 !important;
  pointer-events: none;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 7px;
    align-items: flex-end;
  }
}

.signing-toast {
  width: 290px;

  &--error {
    width: 320px;
  }
  background: #fff;
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
  padding: 11px 13px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: flex-start;
  gap: 10px;
  position: relative;
  overflow: hidden;
  pointer-events: auto;

  &__icon {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &--signing {
      background: #eeedfe;
    }

    &--success {
      background: #e1f5ee;
    }

    &--error {
      background: #fcebeb;
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #eeedfe;
    border-top-color: #534ab7;
    border-radius: 50%;
    animation: signing-spin 0.7s linear infinite;
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__title {
    font-size: 12px;
    font-weight: 500;
    color: #2d2b55;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--success {
      color: #1d9e75;
    }

    &--error {
      color: #e24b4a;
    }
  }

  &__subtitle {
    font-size: 11px;
    color: #aaa;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--error &__subtitle {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.4;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    color: #ccc;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s;
    flex-shrink: 0;

    &:hover {
      color: #888;
    }
  }

  &__progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #f1efe8;
  }

  &__progress-bar {
    height: 100%;
    background: #3ecfb4;
    transition: width 0.3s ease;
    border-radius: 0 1px 1px 0;
  }
}

@keyframes signing-spin {
  to {
    transform: rotate(360deg);
  }
}

// Transition animations
.signing-toast-enter-active {
  transition: all 0.22s ease-out;
}

.signing-toast-leave-active {
  transition: all 0.28s ease-in;
}

.signing-toast-enter {
  opacity: 0;
  transform: translateX(30px);
}

.signing-toast-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
  overflow: hidden;
}

.signing-toast-move {
  transition: transform 0.28s ease;
}
</style>
